import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOpenPlayPublicApi, rsvpOpenPlayApi, cancelRsvpApi, uploadOpenPlayPaymentApi, getOpenPlayPaymentStatusApi, getFacilityRulesApi } from '../lib/api';
import { formatHour, formatPrice, formatDateFriendly, getTodayPHT } from '../lib/format';
import { Users, Clock, MapPin, Check, Loader2, Share2, UserMinus, PartyPopper, AlertCircle, Upload, CreditCard, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function OpenPlayRSVP() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  
  // Track if user has registered (store player_id in localStorage per session)
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [myStatus, setMyStatus] = useState(null); // 'confirmed' or 'waitlist'
  
  // Payment state
  const [showPayment, setShowPayment] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [paymentUploaded, setPaymentUploaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [gcashConfig, setGcashConfig] = useState({});

  const loadSession = useCallback(async () => {
    try {
      const res = await getOpenPlayPublicApi(sessionId);
      setSession(res.data);
      
      // Check if user already registered (from localStorage)
      const stored = localStorage.getItem(`op_${sessionId}`);
      if (stored) {
        const { playerId, status, paymentUploaded: paidStatus } = JSON.parse(stored);
        // Verify player is still in the list
        const inPlayers = res.data.players.some(p => p.id === playerId);
        const inWaitlist = res.data.waitlist.some(w => w.id === playerId);
        if (inPlayers) {
          setMyPlayerId(playerId);
          setMyStatus('confirmed');
          if (paidStatus) setPaymentUploaded(true);
        } else if (inWaitlist) {
          setMyPlayerId(playerId);
          setMyStatus('waitlist');
        } else {
          // Player was removed, clear storage
          localStorage.removeItem(`op_${sessionId}`);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Session not found. This link may be invalid or the session has been removed.');
      } else {
        setError('Failed to load session. Please try again.');
      }
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    loadSession();
    getFacilityRulesApi().then(res => setGcashConfig(res.data)).catch(() => {});
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadSession, 30000);
    return () => clearInterval(interval);
  }, [loadSession]);

  const handleRSVP = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await rsvpOpenPlayApi(sessionId, {
        player_name: playerName.trim(),
        player_phone: playerPhone.trim() || null
      });
      
      // Store registration in localStorage
      localStorage.setItem(`op_${sessionId}`, JSON.stringify({
        playerId: res.data.player_id,
        status: res.data.status
      }));
      
      setMyPlayerId(res.data.player_id);
      setMyStatus(res.data.status);
      
      if (res.data.status === 'confirmed') {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
      }
      
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register');
    }
    setSubmitting(false);
  };

  const handleCancelRSVP = async () => {
    if (!window.confirm('Are you sure you want to cancel your spot?')) return;
    
    setSubmitting(true);
    try {
      await cancelRsvpApi(sessionId, myPlayerId);
      localStorage.removeItem(`op_${sessionId}`);
      setMyPlayerId(null);
      setMyStatus(null);
      setPlayerName('');
      setPaymentUploaded(false);
      setPreviewUrl(null);
      setShowPayment(false);
      toast.success('Your spot has been cancelled');
      loadSession();
    } catch (err) {
      toast.error('Failed to cancel');
    }
    setSubmitting(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !myPlayerId) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      await uploadOpenPlayPaymentApi(sessionId, myPlayerId, file);
      setPaymentUploaded(true);
      // Update localStorage
      const stored = JSON.parse(localStorage.getItem(`op_${sessionId}`) || '{}');
      localStorage.setItem(`op_${sessionId}`, JSON.stringify({ ...stored, paymentUploaded: true }));
      toast.success('Payment proof sent! Staff will verify shortly.');
    } catch (err) {
      toast.error('Upload failed. Try again.');
      setPreviewUrl(null);
    }
    setUploading(false);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Open Play - ${session.date}`,
          text: `Join us for Open Play at ${formatHour(session.start_hour)}!`,
          url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback for clipboard copy
      const copyToClipboard = () => {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success('Link copied to clipboard!');
        } catch (err) {
          toast.info('Copy this link manually');
          prompt('Copy this link:', url);
        }
        document.body.removeChild(textArea);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url)
          .then(() => toast.success('Link copied to clipboard!'))
          .catch(copyToClipboard);
      } else {
        copyToClipboard();
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-open-play" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-status-expired mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Oops!</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const isFull = session.spots_left === 0;
  const isCancelled = session.status === 'cancelled';
  const sessionDate = new Date(session.date + 'T00:00:00');
  const todayPHT = getTodayPHT();
  const isToday = session.date === todayPHT;
  const isPast = sessionDate < new Date(todayPHT + 'T00:00:00');

  return (
    <div className="min-h-screen bg-app-bg" data-testid="open-play-rsvp-page">
      {/* Header */}
      <div className="bg-hero-bg text-white py-6 px-4">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-white/60 text-sm font-medium mb-1">TP PICKLEBALL</p>
          <h1 className="text-2xl font-bold">Open Play</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 -mt-4">
        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden" data-testid="session-card">
          {/* Status Banner */}
          {isCancelled && (
            <div className="bg-status-expired text-white text-center py-2 text-sm font-medium">
              This session has been cancelled
            </div>
          )}
          {isFull && !isCancelled && (
            <div className="bg-status-held text-white text-center py-2 text-sm font-medium">
              Session is full{session.enable_waitlist ? ' — Join the waitlist!' : ''}
            </div>
          )}
          {isPast && !isCancelled && (
            <div className="bg-gray-500 text-white text-center py-2 text-sm font-medium">
              This session has ended
            </div>
          )}

          {/* Session Details */}
          <div className="p-6 space-y-4">
            {/* Date & Time */}
            <div className="text-center pb-4 border-b border-gray-100">
              <p className="text-3xl font-bold text-gray-900" data-testid="session-date">
                {formatDateFriendly(session.date)}
              </p>
              <div className="flex items-center justify-center gap-2 mt-2 text-open-play">
                <Clock className="w-5 h-5" />
                <span className="text-lg font-semibold" data-testid="session-time">
                  {formatHour(session.start_hour)} – {formatHour(session.end_hour)}
                </span>
              </div>
              {isToday && !isPast && (
                <Badge className="mt-2 bg-open-play-tint text-open-play">Today!</Badge>
              )}
            </div>

            {/* Courts & Price */}
            <div className="flex justify-between items-center text-gray-600">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{session.court_names.join(', ')}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">{formatPrice(session.price_per_player)}</span>
                <span className="text-sm text-gray-500">/person</span>
              </div>
            </div>

            {/* Spots Counter */}
            <div className="bg-open-play-tint rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-open-play" />
                  <span className="font-medium text-gray-700">Players</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-open-play" data-testid="player-count">
                    {session.player_count}
                  </span>
                  <span className="text-gray-500">/{session.max_players}</span>
                </div>
              </div>
              {!isFull && (
                <p className="text-sm text-open-play mt-1">
                  {session.spots_left} spot{session.spots_left !== 1 ? 's' : ''} left
                </p>
              )}
            </div>

            {/* Player List */}
            {session.players.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Who's Coming</p>
                <div className="flex flex-wrap gap-2">
                  {session.players.map((player, idx) => (
                    <Badge
                      key={player.id}
                      variant="outline"
                      className={`px-3 py-1 ${player.id === myPlayerId ? 'bg-open-play-tint border-open-play/30 text-open-play' : 'bg-gray-50'}`}
                      data-testid={`player-${idx}`}
                    >
                      {player.name} {player.id === myPlayerId && <Check className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Waitlist */}
            {session.waitlist.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Waitlist</p>
                <div className="flex flex-wrap gap-2">
                  {session.waitlist.map((player, idx) => (
                    <Badge
                      key={player.id}
                      variant="outline"
                      className={`px-3 py-1 ${player.id === myPlayerId ? 'bg-status-held-bg border-status-held/30 text-status-held' : 'bg-gray-50 text-gray-500'}`}
                    >
                      #{idx + 1} {player.name} {player.id === myPlayerId && <Clock className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RSVP Section */}
          <div className="bg-gray-50 p-6 border-t border-gray-100">
            {/* Hidden file input for payment proof */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="payment-proof-input"
            />

            {myPlayerId ? (
              // Already registered
              <div className="space-y-4">
                {myStatus === 'confirmed' ? (
                  <>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-open-play-tint">
                        <PartyPopper className="w-8 h-8 text-open-play" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mt-3">You're in!</h3>
                      <p className="text-gray-600 text-sm">See you on the court</p>
                    </div>

                    {/* GCash Payment Section */}
                    {!paymentUploaded ? (
                      <div className="bg-open-play-tint rounded-2xl p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-open-play" />
                          <p className="text-sm font-semibold text-open-play-active">Send Payment via GCash</p>
                        </div>
                        
                        <div className="bg-white rounded-xl p-3 space-y-3">
                          <div>
                            <p className="text-xs text-gray-400">GCash Number</p>
                            <div className="flex items-center justify-between">
                              <p className="text-base font-semibold text-gray-900 font-mono">{gcashConfig.gcash_number || '09XX XXX XXXX'}</p>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText((gcashConfig.gcash_number || '').replace(/\s/g, ''));
                                  toast.success('GCash number copied!');
                                }}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-open-play-tint hover:bg-open-play-tint transition-colors text-xs font-medium text-open-play"
                                data-testid="copy-gcash-number-btn"
                              >
                                <Copy className="w-3 h-3" />
                                Copy
                              </button>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Account Name</p>
                            <p className="text-base font-semibold text-gray-900">{gcashConfig.gcash_account_name || 'TP Pickleball'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Amount</p>
                            <p className="text-base font-semibold text-gray-900">{formatPrice(session.price_per_player)}</p>
                          </div>
                          {gcashConfig.gcash_qr_image && (
                            <div className="flex justify-center pt-1">
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${gcashConfig.gcash_qr_image}`}
                                alt="GCash QR Code"
                                className="rounded-xl border border-open-play/20 max-w-[180px] w-full"
                                data-testid="gcash-qr-code"
                              />
                            </div>
                          )}
                        </div>

                        {previewUrl && (
                          <div className="rounded-xl overflow-hidden border border-open-play/20">
                            <img src={previewUrl} alt="Payment proof" className="w-full max-h-48 object-contain bg-white" />
                          </div>
                        )}

                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="w-full bg-open-play hover:bg-open-play-hover text-white rounded-full py-5"
                          data-testid="upload-payment-btn"
                        >
                          {uploading ? (
                            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Upload GCash Screenshot</>
                          )}
                        </Button>
                        <p className="text-xs text-center text-open-play">
                          You can also pay at the venue
                        </p>
                      </div>
                    ) : (
                      <div className="bg-open-play-tint rounded-2xl p-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-open-play">
                          <Check className="w-5 h-5" />
                          <span className="font-semibold">Payment proof uploaded!</span>
                        </div>
                        <p className="text-sm text-open-play mt-1">Staff will verify shortly</p>
                      </div>
                    )}

                    <div className="text-center pt-2">
                      <Button
                        onClick={handleCancelRSVP}
                        variant="ghost"
                        className="text-status-expired hover:bg-status-expired-bg text-sm"
                        disabled={submitting}
                        data-testid="cancel-rsvp-btn"
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        {submitting ? 'Cancelling...' : 'Cancel My Spot'}
                      </Button>
                    </div>
                  </>
                ) : (
                  // Waitlist
                  <div className="text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-status-held-bg">
                      <Clock className="w-8 h-8 text-status-held" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">You're on the waitlist</h3>
                      <p className="text-gray-600 text-sm">We'll move you up if a spot opens</p>
                    </div>
                    <Button
                      onClick={handleCancelRSVP}
                      variant="outline"
                      className="rounded-full text-status-expired border-status-expired/20 hover:bg-status-expired-bg"
                      disabled={submitting}
                      data-testid="cancel-rsvp-btn"
                    >
                      <UserMinus className="w-4 h-4 mr-2" />
                      {submitting ? 'Cancelling...' : 'Leave Waitlist'}
                    </Button>
                  </div>
                )}
              </div>
            ) : isCancelled || isPast ? (
              // Session cancelled or past
              <div className="text-center py-4">
                <p className="text-gray-500">Registration is closed</p>
              </div>
            ) : (
              // Registration form
              <form onSubmit={handleRSVP} className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600">Your Name</Label>
                  <Input
                    data-testid="rsvp-name-input"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Enter your name"
                    className="rounded-xl mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Phone (optional)</Label>
                  <Input
                    data-testid="rsvp-phone-input"
                    value={playerPhone}
                    onChange={(e) => setPlayerPhone(e.target.value)}
                    placeholder="+63 9XX XXX XXXX"
                    className="rounded-xl mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className={`w-full rounded-full py-5 text-white ${
                    isFull
                      ? 'bg-status-held hover:opacity-90'
                      : 'bg-open-play hover:bg-open-play-hover'
                  }`}
                  disabled={submitting || (!session.enable_waitlist && isFull)}
                  data-testid="rsvp-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : isFull ? (
                    session.enable_waitlist ? 'Join Waitlist' : 'Session Full'
                  ) : (
                    "I'm In!"
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Share Button */}
        <div className="mt-6 text-center">
          <Button
            onClick={handleShare}
            variant="ghost"
            className="text-open-play hover:text-open-play-hover hover:bg-open-play-tint rounded-full"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share this session
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">
            Powered by TP PICKLEBALL
          </p>
        </div>
      </div>
    </div>
  );
}
